<!--
  Task-list success illustration used by Todoist, Asana and any other
  integration whose success screen shows a preview of incoming tasks.

  choice === "optionA" → all tasks mode
  choice === "optionB" / "optionC" → specific project/filter mode (name in
  filterName)
-->
<template>
  <div class="tasks-illus">
    <div class="tasks-preview">
      <div
        v-for="row in rows"
        :key="row.name"
        class="task-row"
        :class="{ highlight: row.highlight, completed: row.completed }"
      >
        <span class="task-check" :class="{ checked: row.completed }" />
        <span class="task-name">{{ row.name }}</span>
        <span class="task-project">{{ row.project }}</span>
      </div>
    </div>
    <div class="tasks-routine-badge">
      <span class="tasks-routine-icon">→</span>
      <span>Routine inbox</span>
    </div>
  </div>

  <p class="success-caption">
    <template v-if="choice === 'optionA'">
      Your {{ integrationLabel }} tasks will automatically appear in your
      Routine inbox.
    </template>
    <template v-else>
      Tasks in <strong>{{ filterName }}</strong> will automatically appear in
      your Routine inbox.
    </template>
  </p>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";

import type { IllustrationRow } from "./useIntegrationSyncWorkflow";

export default defineComponent({
  props: {
    choice: { type: String, required: true },
    filterName: { type: String, default: "" },
    integrationLabel: { type: String, required: true },
    rows: { type: Array as PropType<IllustrationRow[]>, default: () => [] },
  },
});
</script>

<style scoped lang="scss">
@use "../../../styles/settings.scss";

.tasks-illus {
  flex: 1;
  border-radius: $border-radius-m;
  overflow: hidden;
  position: relative;
  min-height: 160px;
  @include themed() {
    border: 1px solid t($grey-10);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    @include themed() {
      background: linear-gradient(to bottom, transparent 40%, t($bg) 100%);
    }
  }
}

.tasks-preview {
  display: flex;
  flex-direction: column;
  opacity: 0.78;
  pointer-events: none;
  user-select: none;
}

.tasks-routine-badge {
  position: absolute;
  bottom: 16px;
  inset-inline-end: 14px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  pointer-events: none;
  animation: badge-pulse 2s ease-out infinite;
  @include themed() {
    background: t($bg);
    color: t($grey-80);
    border: 1px solid t($grey-30);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.tasks-routine-icon {
  font-size: 12px;
  line-height: 1;
}

@keyframes badge-pulse {
  0% {
    box-shadow:
      0 0 0 0 rgba(101, 111, 198, 0.45),
      0 2px 8px rgba(0, 0, 0, 0.1);
  }
  60% {
    box-shadow:
      0 0 0 10px rgba(101, 111, 198, 0),
      0 2px 8px rgba(0, 0, 0, 0.1);
  }
  100% {
    box-shadow:
      0 0 0 0 rgba(101, 111, 198, 0),
      0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.task-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  height: 40px;
  font-size: 11px;
  @include themed() {
    border-bottom: 1px solid t($grey-05);
  }

  &.highlight {
    @include themed() {
      background: t($grey-03);
    }
    .task-name {
      font-weight: 600;
    }
  }

  &.completed {
    opacity: 0.4;
    text-decoration: line-through;
  }
}

.task-check {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  @include themed() {
    border: 1.5px solid t($grey-30);
  }

  &.checked {
    @include themed() {
      background: t($grey-15);
    }
  }
}

.task-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @include themed() {
    color: t($primary);
  }
}

.task-project {
  @extend %font-caption-regular;
  padding: 2px 6px;
  border-radius: $border-radius-s;
  flex-shrink: 0;
  @include themed() {
    background: t($grey-05);
    color: t($grey-60);
  }
}

.success-caption {
  @extend %font-body-regular;
  margin: 0;
  @include themed() {
    color: t($grey-50);
  }
}
</style>
